import { Head, usePage } from '@inertiajs/react'
import {
  Badge,
  Card,
  Container,
  Group,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { timeAgo } from '@/lib/session-utils'
import type {
  CostByModelProps,
  CostByProviderProps,
  DailyCostProps,
  MeteredResourceProps,
  UsageCostsPageProps,
  UsageEventProps,
  UsageSummaryProps,
} from '@/types'

function formatCurrency(value: number): string {
  return `$${value.toFixed(4)}`
}

function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return value.toLocaleString()
}

function formatDuration(startedAt: string, stoppedAt: string | null): string {
  const start = new Date(startedAt).getTime()
  const end = stoppedAt ? new Date(stoppedAt).getTime() : Date.now()
  const seconds = Math.floor((end - start) / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

function SummaryCards({ summary }: { summary: UsageSummaryProps }) {
  return (
    <SimpleGrid cols={{ base: 2, md: 4 }}>
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed" tt="uppercase" fw={600}>Cost This Month</Text>
        <Text size="xl" fw={700} mt={4}>{formatCurrency(summary.total_cost_this_month)}</Text>
      </Card>
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed" tt="uppercase" fw={600}>Cost (30 Days)</Text>
        <Text size="xl" fw={700} mt={4}>{formatCurrency(summary.total_cost_30_days)}</Text>
      </Card>
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed" tt="uppercase" fw={600}>Total Tokens</Text>
        <Text size="xl" fw={700} mt={4}>
          {formatTokens(summary.total_input_tokens + summary.total_output_tokens + summary.total_thinking_tokens)}
        </Text>
        <Text size="xs" c="dimmed">
          {formatTokens(summary.total_input_tokens)} in / {formatTokens(summary.total_output_tokens)} out
          {summary.total_thinking_tokens > 0 && ` / ${formatTokens(summary.total_thinking_tokens)} thinking`}
        </Text>
      </Card>
      <Card withBorder padding="lg">
        <Text size="sm" c="dimmed" tt="uppercase" fw={600}>LLM Calls</Text>
        <Text size="xl" fw={700} mt={4}>{summary.total_events.toLocaleString()}</Text>
        {summary.active_resources_count > 0 && (
          <Text size="xs" c="dimmed">{summary.active_resources_count} active resource(s)</Text>
        )}
      </Card>
    </SimpleGrid>
  )
}

function CostByModelTable({ data }: { data: CostByModelProps[] }) {
  if (data.length === 0) return <Text c="dimmed">No usage data yet.</Text>

  const maxCost = Math.max(...data.map(r => r.total_cost))

  return (
    <Card withBorder>
      <Text fw={600} mb="sm">Cost by Model</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Model</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Cost</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Calls</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Input</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Output</Table.Th>
            <Table.Th style={{ width: 120 }}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((row) => (
            <Table.Tr key={row.model_id}>
              <Table.Td>
                <Text size="sm" ff="monospace">{row.model_id}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm" fw={600}>{formatCurrency(row.total_cost)}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm">{row.event_count}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm">{formatTokens(row.total_input_tokens)}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm">{formatTokens(row.total_output_tokens)}</Text>
              </Table.Td>
              <Table.Td>
                {maxCost > 0 && (
                  <Progress value={(row.total_cost / maxCost) * 100} size="sm" />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

function CostByProviderTable({ data }: { data: CostByProviderProps[] }) {
  if (data.length === 0) return null

  return (
    <Card withBorder>
      <Text fw={600} mb="sm">Cost by Provider</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Provider</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Cost</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Calls</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((row) => (
            <Table.Tr key={row.provider}>
              <Table.Td>
                <Badge variant="light" size="sm">{row.provider}</Badge>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm" fw={600}>{formatCurrency(row.total_cost)}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm">{row.event_count}</Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

function DailyCostsTable({ data }: { data: DailyCostProps[] }) {
  if (data.length === 0) return null

  const maxCost = Math.max(...data.map(r => r.cost))

  return (
    <Card withBorder>
      <Text fw={600} mb="sm">Daily Costs (Last 30 Days)</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Cost</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Calls</Table.Th>
            <Table.Th style={{ width: 200 }}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.map((row) => (
            <Table.Tr key={row.date}>
              <Table.Td>
                <Text size="sm">{row.date}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm" fw={600}>{formatCurrency(row.cost)}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm">{row.event_count}</Text>
              </Table.Td>
              <Table.Td>
                {maxCost > 0 && (
                  <Progress value={(row.cost / maxCost) * 100} size="sm" />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

function RecentEventsTable({ events }: { events: UsageEventProps[] }) {
  if (events.length === 0) return <Text c="dimmed">No usage events recorded yet.</Text>

  return (
    <Card withBorder>
      <Text fw={600} mb="sm">Recent Events</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Session</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Tokens</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Cost</Table.Th>
            <Table.Th>Time</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {events.map((event) => {
            const meta = event.metadata as Record<string, number | string>
            const inputTokens = Number(meta.input_tokens || 0)
            const outputTokens = Number(meta.output_tokens || 0)
            const cost = Number(meta.estimated_cost || 0)
            return (
              <Table.Tr key={event.id}>
                <Table.Td>
                  <Badge variant="light" size="sm">{event.event_type}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">{String(meta.model_id || '-')}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" lineClamp={1}>{event.session_title || '-'}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">{formatTokens(inputTokens + outputTokens)}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm" fw={600}>{cost > 0 ? formatCurrency(cost) : '-'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">{timeAgo(event.recorded_at)}</Text>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

function MeteredResourcesTable({ resources }: { resources: MeteredResourceProps[] }) {
  if (resources.length === 0) return null

  return (
    <Card withBorder>
      <Text fw={600} mb="sm">Metered Resources</Text>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Resource ID</Table.Th>
            <Table.Th>Session</Table.Th>
            <Table.Th>Duration</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Started</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {resources.map((resource) => (
            <Table.Tr key={resource.id}>
              <Table.Td>
                <Badge variant="light" size="sm">{resource.resource_type}</Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ff="monospace">{resource.resource_id}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">{resource.session_title || '-'}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatDuration(resource.started_at, resource.stopped_at)}</Text>
              </Table.Td>
              <Table.Td>
                <Badge color={resource.active ? 'green' : 'gray'} variant="light" size="sm">
                  {resource.active ? 'Active' : 'Stopped'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">{timeAgo(resource.started_at)}</Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

export default function UsageCosts() {
  const {
    organization,
    summary,
    cost_by_model,
    cost_by_provider,
    daily_costs,
    recent_events,
    metered_resources,
  } = usePage<UsageCostsPageProps>().props

  return (
    <>
      <Head title="Usage" />

      <Container size="xl" py="md">
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Usage</Title>
              <Text c="dimmed" mt={4}>{organization.name}</Text>
            </div>
          </Group>

          <SummaryCards summary={summary} />

          <SimpleGrid cols={{ base: 1, lg: 2 }}>
            <CostByModelTable data={cost_by_model} />
            <CostByProviderTable data={cost_by_provider} />
          </SimpleGrid>

          <DailyCostsTable data={daily_costs} />
          <RecentEventsTable events={recent_events} />
          <MeteredResourcesTable resources={metered_resources} />
        </Stack>
      </Container>
    </>
  )
}
